/**
 * Utility functions for the Explorer MRT component
 */

import {ResourceType} from '../../../@types/resource-type';
import {ExplorerDatasetType, ExtendedTreeViewItem} from './config/explorer-mrt-types';
import ResourceBase from '../../../@types/resource-base';

/**
 * Creates a ResourceBase from an ExtendedTreeViewItem
 */
export const createResourceFromTreeItem = (
    item: ExtendedTreeViewItem,
    parentId?: number
): ResourceBase => {
    // Handle prefixed IDs (e.g., 'group-123'), but not UUIDs (e.g., Foundry event uniqueIds)
    let resourceId = item.id;
    if (resourceId.includes('-') && item.type !== ResourceType.FOUNDRY_EVENTS) {
        const parts = resourceId.split('-');
        resourceId = parts[parts.length - 1]; // Get the numeric part after the prefix
    }

    // For Event Sequences and other resources, prefer the numeric ID from the object
    // This is crucial for lazy-loaded items where item.id might be the uniqueId
    const numericId = item.object?.id || parseInt(resourceId) || undefined;
    
    const resource = new ResourceBase(
        item.type,
        numericId,
        parentId
    );

    // Foundry events are fetched by uniqueId, not numeric id
    if (item.type === ResourceType.FOUNDRY_EVENTS && item.object?.uniqueId) {
        resource.uniqueId = item.object.uniqueId;
    }

    // Add container information for nested resources
    if (item.object?.containerId) {
        (resource as any).containerId = item.object.containerId;
    }

    return resource;
};

/**
 * Gets the label for the create button based on dataset type
 */
export const getCreateButtonLabel = (dataset: ExplorerDatasetType): string => {
    switch (dataset) {
        case ResourceType.SAFETY_MODELS:
            return 'New Safety Model';
        case ResourceType.GROUPS:
            return 'New Working Area';
        case ResourceType.WORKSHOPS:
            return 'New Workshop';
        case ResourceType.RISK_FORECASTS:
            return 'New Risk Forecast';
        case ResourceType.OPERATIONAL_CHANGES:
            return 'New Operational Change';
        case ResourceType.PERFORMANCE_SHAPING_FACTORS:
            return 'New PSF Template';
        case ResourceType.USERS:
            return 'New User';
        case ResourceType.SERVICE_USER_ACCOUNTS:
            return 'New Service User';
        case ResourceType.NEW_OCCURRENCE_REPORTS:
            return 'New Report';
        case ResourceType.OCCURRENCE_REPORT_DESCRIPTORS:
            return 'New Descriptor';
        case ResourceType.TRAFFIC_MODELS:
            return 'New Traffic Model';
        default:
            return 'New';
    }
};

/**
 * Checks if a dataset supports creation of new items
 */
export const canCreateNewItem = (dataset: ExplorerDatasetType): boolean => {
    // Read-only datasets — creation is not supported
    if (dataset === ResourceType.NEW_OCCURRENCE_REPORTS ||
        dataset === ResourceType.FOUNDRY_EVENTS) {
        return false;
    }

    // All other datasets support creation
    return true;
};